import Roster from './Roster.js'
import RosterBase from './Roster.vue'
import MonthlyData from './MonthlyData.vue'
import { hasStampedFacts, serverTodayOf } from '../utils/rosterStampedRow'

/**
 * 勤務表の画面の部品を、**画面に出さずに**動かすための包み。
 *
 * ## 何のためか
 *
 * 打刻（ダッシュボードの退勤）のあと、勤務表で同じ時刻を入れて［保存］したときと同じ処理を流す。
 * 計算の式・検証・保存の本文づくりは、勤務表の部品（Roster.js＝Roster.vue を extends した包み、
 * 行ごとの子の部品、MonthlyData.vue）にある1か所だけ。ここには式を1つも持たない。
 * 部品そのものを画面に付けずに立て、月を読み込ませ、［保存］と同じ道（register）を通す。
 * 流す手順は rosterHeadlessSync.js にある。
 *
 * ## 見える勤務表と違うのは、この5つだけ
 *
 * 1. **知らせを画面に出さない。**確認の小窓（alert）の代わりに、文を貯める。
 *    月の合計の部品（MonthlyData）の知らせも、同じ所へ貯める。
 * 2. **保存のあとに読み直さない。**結果は呼んだ側が受け取るので、表を開き直す必要が無い。
 * 3. **指定された年月を読む**（見える勤務表は、年月の選択欄と［表示］で読む）。
 * 4. **今日より後で、まだ行が無い日を、保存の本文へ入れない**（headlessSendsDay）。
 *    見える勤務表の［保存］は、月の全部の日を保存する（その日の行を作る）。打刻のあとの自動の保存が
 *    同じことをすると、月の初めに退勤を打刻しただけで、その月の残りの日すべてに、その時点の案件と
 *    勤怠の行が出来る。そのあとで所属の案件・会社の休日・稼働曜日を変えても、残りの日に効かなくなる
 *    （行がある日は、行に保存された案件と勤怠が優先されるため）。利用者が見ていない所で、
 *    先の日の行を作らない。行の中身（式・項目・順番）と月の合計は、見える勤務表と同じ。
 * 5. **「今日」は、サーバの今日（一覧の応答の server_today）で決める**（rosterTodayKey・saveTodayKey）。
 *    見える勤務表は端末の日付で「予定（未来日）」を決め、未来日の時刻を空にして保存する。端末の日付が
 *    サーバとずれていると、見えない所で流す保存が、サーバから見て明日の、時刻の無い行を作る
 *    （端末が進んでいるとき。サーバの未来日の検査は、時刻のある行しか断らない）か、今日打刻した時刻を
 *    空で上書きしようとする（端末が遅れているとき）。どちらも利用者は見ていない。だから、見えない所では
 *    サーバの今日で、4 の「今日より後」も、未来日として時刻を送らない日も決める。
 *    応答がサーバの今日を持たない（その項目を返さない版のサーバ）ときは、流す処理が保存へ進まない
 *    （rosterHeadlessSync.js の unsupported）。読み込みのあいだだけ、端末の日付で動く。
 *
 * 打刻由来の行の扱いは Roster.js にあり、見える勤務表と同じコードが動く。
 */

// 月の合計の部品。知らせの出し先と、本文へ入れる日だけを差し替える
// （式・本文づくり・保存は MonthlyData.vue のまま）。
const HeadlessMonthlyData = {
  name: 'MonthlyData',
  extends: MonthlyData,
  inject: {
    headlessNotify: { default: null },
    headlessSendsDay: { default: null },
    headlessTodayKey: { default: null }
  },
  methods: {
    notify (message) {
      if (this.headlessNotify) this.headlessNotify(message)
    },

    // その日の行を、保存の本文へ入れるか。決めるのは親（下の headlessSendsDay）。
    includesDayInSaveBody (index) {
      return this.headlessSendsDay ? this.headlessSendsDay(index) !== false : true
    },

    // 保存の本文で「未来日なので時刻を送らない日」を決める今日。親（下の rosterTodayKey）と同じ日。
    saveTodayKey () {
      return this.headlessTodayKey ? this.headlessTodayKey() : MonthlyData.methods.saveTodayKey.call(this)
    }
  }
}

const hasRowMark = value => value !== null && value !== undefined

export default {
  name: 'RosterHeadless',
  extends: Roster,

  components: {
    MonthlyData: HeadlessMonthlyData
  },

  provide () {
    return {
      headlessNotify: message => this.notify(message),
      headlessSendsDay: index => this.headlessSendsDay(index),
      headlessTodayKey: () => this.rosterTodayKey()
    }
  },

  created () {
    // 画面なら確認の小窓で出ていた文。
    this.headlessNotices = []
    // 読み込みで、案件の基準（定時・休憩・猶予）を取り直せなかった。
    this.headlessBasicsFailed = false
    // 流す処理が打ち切られた（時間切れ）。
    this.headlessCancelled = false
    // 流す処理が「ここから先へ進んではいけないか」を答える口（rosterHeadlessSync.js が立てた直後に入れる）。
    // 呼んだ側の打ち切りも、ここで分かる。
    this.headlessStopCheck = null
    // 一覧の応答が持つサーバの今日を、どの行の配列から読んだか（headlessServerToday の控え）。
    this.headlessServerTodayOf = undefined
    this.headlessServerTodayValue = null
  },

  methods: {
    // 入力区分の印は画面に出すもの。ここ（画面を出さない保存）では打刻の記録を読まない。
    // 打刻のあとの保存の通信を増やさない。
    loadStampEvents () {
      return Promise.resolve()
    },
    // 知らせは画面に出さず、貯める。
    notify (message) {
      const text = String(message)
      this.headlessNotices.push(text)
      this.$emit('headless-notice', text)
    },

    /*
     * 日ごとの判定（過去日・当日・予定）と、保存の本文の未来日の「今日」：サーバの今日。
     *
     * 一覧の応答の全部の行に載ってくる server_today（サーバが未来日の時刻を隠すときと同じ今日）。
     * 載っていない応答（その項目を返さない版のサーバ）では、端末の日付のまま（見える勤務表と同じ）。
     * その応答では、流す処理が保存へ進まない（rosterHeadlessSync.js が headlessServerToday を見て止める）。
     */
    rosterTodayKey () {
      return this.headlessServerToday() || RosterBase.methods.rosterTodayKey.call(this)
    },

    // 一覧の応答が持つサーバの今日（'YYYY-MM-DD'）。持たなければ null。
    // 表を描くたびに何度も呼ばれるので、読み込んだ行（items）が差し替わったときだけ読み直す。
    headlessServerToday () {
      const rows = this.items
      if (this.headlessServerTodayOf !== rows) {
        this.headlessServerTodayOf = rows
        this.headlessServerTodayValue = serverTodayOf(rows)
      }
      return this.headlessServerTodayValue
    },

    // 保存のあと（と、保存を断られて読み直すとき）に、表を開き直さない。
    reloadShownMonth () {},

    // 保存の前の検証（勤務表と同じ）。流す処理が打ち切られていたら、検証が通っても保存へ進ませない。
    async validateBeforeSave () {
      const ok = await RosterBase.methods.validateBeforeSave.call(this)
      const stopped = this.headlessCancelled ||
        (typeof this.headlessStopCheck === 'function' && this.headlessStopCheck() === true)
      return ok && !stopped
    },

    // 指定の年月を読む（勤務表で年月を選んで［表示］を押したのと同じ処理）。
    loadMonth (year, month) {
      this.headlessNotices = []
      this.headlessBasicsFailed = false
      this.year = Number(year)
      this.month = Number(month)
      this.callList()
    },

    /*
     * 案件の基準を取り直せなかったことを、その場で覚えておく。
     *
     * 勤務表は、取り直しに失敗すると知らせを出すが、表は出たままになり、読み込みも「終わった」扱いになる。
     * しかも知らせが出るのは、失敗の報告を送り終えたあと（読み込みが終わったあと）になる。
     * 知らせを待たずに保存へ進むと、古い基準のまま計算した値を保存してしまう。
     *
     * 取り直しに成功したときだけ、基準の配列が新しいものへ差し替わる（Roster.vue）。
     * 差し替わっていなければ、失敗している。
     */
    hydrateDailyBasicsByProject () {
      const before = this.startBasic
      const asked = this.projectId.some(value => value != null)
      return RosterBase.methods.hydrateDailyBasicsByProject.call(this).then(result => {
        if (asked && this.startBasic === before) this.headlessBasicsFailed = true
        return result
      })
    },

    /*
     * その日（index は 0 始まり）の行を、保存の本文へ入れるか。
     *
     * 入れないのは、「サーバの今日より後の日」で「まだ行が無い日」だけ（新しい行を作ってよい日の終点は、
     * サーバの今日。isFutureIndex は、ここでは rosterTodayKey＝サーバの今日で決まる。端末の日付は使わない）。
     * - 今日までの日は、行が無くても入れる（見える勤務表と同じ。打刻した日と同じ月の、
     *   過ぎた日の行は、今までどおり作る）。
     * - すでに行がある未来の日（承認された休暇・前もって入れた欠勤など）は、入れる。
     *   サーバは、月の合計の確かめで、送られた行から未来日の欠勤を数える。外すと、
     *   欠勤を前もって入れてある月の保存が断られる。
     *
     * 行があるかは、一覧が返した行の番号（id）で見る。行の版（dailyVersion）しか無い行も、
     * 行がある扱いにする（入れる側へ倒す。入れれば、今までと同じ本文になる）。
     */
    headlessSendsDay (index) {
      if (!this.isFutureIndex(index)) return true
      const ids = Array.isArray(this.id) ? this.id : []
      const versions = Array.isArray(this.dailyVersion) ? this.dailyVersion : []
      return hasRowMark(ids[index]) || hasRowMark(versions[index])
    },

    // 保存の本文へ入れる日が、1日でもあるか。1日も無ければ、送らない（空の本文を送らない）。
    headlessSendsAnyDay () {
      return (Array.isArray(this.monthDay) ? this.monthDay : []).some((_, index) => this.headlessSendsDay(index))
    },

    // 月の合計の部品（表が出ていないあいだは null）。
    headlessMonthly () {
      return this.$refs.monthly || null
    },

    // 一覧の応答が、打刻の印を持っているか。
    headlessHasStampedFacts () {
      return (Array.isArray(this.items) ? this.items : []).some(hasStampedFacts)
    },

    // サーバが時刻を隠して返した行があるか。
    headlessHasHiddenActual () {
      return (Array.isArray(this.items) ? this.items : []).some(row => Boolean(row) && row.actual_hidden === true)
    },

    /*
     * 一覧が開始か終了を返したのに、この部品が未来日として扱っている行があるか。
     *
     * 勤務表は、未来日の行の時刻を空にして開き、空のまま保存する。ここで未来日を決める今日は
     * サーバの今日（rosterTodayKey）なので、端末の日付が遅れていても、今日打刻した行は未来日に
     * ならない（サーバは、自分の今日より後の行の時刻を隠して返す）。だから、サーバの今日が載った応答では、
     * ここは当たらないはず。それでも「一覧が返した時刻を、画面が空にした」が起きたら、保存すると
     * 打刻した時刻が空で上書きされて消えるので、止める（守りとして残す）。
     */
    headlessDropsReturnedTimes () {
      const has = value => value !== null && value !== undefined && String(value) !== ''
      return (Array.isArray(this.items) ? this.items : []).some((row, index) => (
        Boolean(row) && (has(row.start_time) || has(row.end_time)) && this.isFutureIndex(index)
      ))
    },

    // 保存の本文を決める値の写し。出そろったかを、これが続けて同じかで見る。
    headlessSnapshot () {
      const monthly = this.headlessMonthly()
      return JSON.stringify({
        loading: this.loading,
        displayFlg: this.displayFlg,
        start: this.start,
        end: this.end,
        sendEnd: this.send_end,
        rest: this.rest,
        nightRest: this.nightRest,
        attendance1: this.attendance1,
        attendance2: this.attendance2,
        remarks: this.remarks,
        projectId: this.projectId,
        work: this.displayFlg ? this.dailyWorkResult : null,
        over: this.overTimeData,
        night: this.nightTimeData,
        holiday: this.holiDayWorkData,
        deduct: this.deductionData,
        monthly: monthly ? {
          actualTime: monthly.actualTime,
          overTotal: monthly.overTotal,
          nightTotal: monthly.nightTotal,
          holidayTotal: monthly.holidayTotal,
          absentTime: monthly.absentTime,
          lateCount: monthly.lateCount,
          earlyCount: monthly.earlyCount,
          duration: monthly.duration,
          durationTime: monthly.durationTime,
          actual: monthly.actual,
          getInstead: monthly.get_instead,
          usePaid: monthly.usePaid,
          useInstead: monthly.useInstead
        } : null
      })
    },

    /*
     * 日ごとの値を足した合計と、月の合計が合っているか。合っていない項目の名前を返す（空なら全部合っている）。
     *
     * 月の合計は、画面が送った値がそのまま保存される。日ごとの値とずれたまま送ると、
     * 勤務表と月次集計で数字が食い違ったままになる。
     * 足し方は月の合計の部品のもの（calcTotal）をそのまま使う。ここに足し方の写しを持たない。
     */
    headlessTotalMismatches () {
      const monthly = this.headlessMonthly()
      if (!monthly) return ['月の合計']
      const pairs = [
        ['時間外', this.overTimeData, monthly.overTotal],
        ['夜勤', this.nightTimeData, monthly.nightTotal],
        ['法定休日', this.holiDayWorkData, monthly.holidayTotal],
        ['控除', this.deductionData, monthly.absentTime],
        ['業務', this.dailyWorkResult, monthly.actualTime]
      ]
      return pairs
        .filter(([, daily, total]) => monthly.calcTotal(daily) !== total)
        .map(([name]) => name)
    },

    // いま各欄の下に出ている検証の文（勤務表なら赤字で出ている文）。
    headlessFieldErrors () {
      if (!this.$el || !this.$el.querySelectorAll) return []
      return Array.from(this.$el.querySelectorAll('.roster-field-error'))
        .map(element => String(element.textContent || '').trim())
        .filter(Boolean)
    }
  }
}
